import React, {
    memo,
    useEffect,
    useId,
    useMemo,
    useSyncExternalStore,
} from "react";
import { Elements } from "@stripe/react-stripe-js";
import { useLoadStripe } from "@hooks/billing/useLoadStripe";
import { useStripeOptions } from "@hooks/billing/useStripeOptions";
import { StandardModal } from "@design-system/Modal/Modal";
import { ConvertTrialModal } from "./ConvertTrialModal";
import { DowngradeModalContent } from "./DowngradeModal/DowngradeModalContent";
import { UpgradeSubscriptionModal } from "./UpgradeSubscriptionModal/UpgradeSubscriptionModal";
import { usePlanManagementModal } from "./PlanManagementContext";
import styles from "./PlanManagement.module.scss";

const PlanManagementModalContent: React.FC = () => {
    const {
        upgradeModal,
        downgradeModal,
        convertModal,
        checkoutData,
        onHideUpgrade,
        onHideDowngrade,
        onHideConvert,
    } = usePlanManagementModal();

    const stripePromise = useLoadStripe();
    const defaultOptions = useStripeOptions();
    const stripeOptions = useMemo(
        () => ({
            ...defaultOptions,
            clientSecret: checkoutData?.clientSecret,
        }),
        [checkoutData?.clientSecret, defaultOptions],
    );

    return (
        <>
            <StandardModal
                size="xl"
                show={!!upgradeModal}
                onHide={onHideUpgrade}
                className={styles.planManagementModal}
                dialogClassName={styles.planManagementDialog}
            >
                {upgradeModal && !!checkoutData && (
                    <Elements stripe={stripePromise} options={stripeOptions}>
                        <UpgradeSubscriptionModal
                            {...upgradeModal}
                            intentId={checkoutData.intentId}
                        />
                    </Elements>
                )}
            </StandardModal>

            <StandardModal
                size="xl"
                show={convertModal}
                onHide={onHideConvert}
                className={styles.planManagementModal}
                dialogClassName={styles.planManagementDialog}
            >
                {convertModal && !!checkoutData && (
                    <Elements stripe={stripePromise} options={stripeOptions}>
                        <ConvertTrialModal intentId={checkoutData.intentId} />
                    </Elements>
                )}
            </StandardModal>

            <StandardModal
                size="lg"
                show={!!downgradeModal}
                onHide={onHideDowngrade}
                className={styles.planChangeModal}
            >
                {downgradeModal && (
                    <DowngradeModalContent
                        downgradingToPlan={downgradeModal}
                        onHide={onHideDowngrade}
                    />
                )}
            </StandardModal>
        </>
    );
};

export const PlanManagementModals = memo(() => {
    const reactId = useId();
    const id = `plan-management-modal-host-${reactId}`;

    const { registerAsModalHost, subscribeToHostChanges, getActiveHostId } =
        usePlanManagementModal();

    useEffect(() => registerAsModalHost(id), [id, registerAsModalHost]);

    const activeHostId = useSyncExternalStore(
        subscribeToHostChanges,
        getActiveHostId,
    );

    if (activeHostId !== id) {
        return null;
    }

    return <PlanManagementModalContent />;
});
